import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Typography,
  Grid,
  Paper,
  IconButton,
  Button,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  PendingActions as PendingIcon,
  Engineering as EngineeringIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfigEmpresa } from '../context/ConfigEmpresaContext';
import { useEstados } from '../context/EstadosContext';
import axios from 'axios';
import { useSocket } from '../context/SocketContext';
import DetalleDialog from '../components/DetalleDialog';
import appConfig from '../config';
import { formatearFechaLocal } from '../utils/dateUtils';

// Importar componentes modulares
import ActualizarEstadoDialog from '../components/tablero/ActualizarEstadoDialog';
import EstadisticasCard from '../components/tablero/EstadisticasCard';
import KanbanColumn from '../components/tablero/KanbanColumn';

/**
 * Componente principal del Tablero de Control Kanban
 * Gestiona la visualización y actualización de órdenes de servicio en tiempo real
 */
function TableroControl() {
  // Estados locales
  const [ordenes, setOrdenes] = useState({});
  const [locales, setLocales] = useState([]);
  const [trabajadores, setTrabajadores] = useState([]);
  const [filtroLocal, setFiltroLocal] = useState('');
  const [filtroTrabajador, setFiltroTrabajador] = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroFecha, setFiltroFecha] = useState('hoy');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [loading, setLoading] = useState(false);
  const [ultimaActualizacion, setUltimaActualizacion] = useState(new Date());
  const [selectedOrden, setSelectedOrden] = useState(null);
  const [openDetail, setOpenDetail] = useState(false);
  const [openUpdate, setOpenUpdate] = useState(false);

  // Hooks
  const { user } = useAuth();
  const { socket } = useSocket();
  const { config, loading: configLoading } = useConfigEmpresa();
  const { estados, loading: estadosLoading } = useEstados();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Ref para controlar si ya nos unimos a la sala
  const joinedCompanyRef = useRef(false);
  const currentCompanyRef = useRef(null);
  const empresaIdRef = useRef(user?.id_empresa);

  // Actualizar ref cuando cambie el id_empresa
  useEffect(() => {
    empresaIdRef.current = user?.id_empresa;
  }, [user?.id_empresa]);

  /**
   * Calcula el rango de fechas según el filtro seleccionado
   * Utiliza zona horaria local (Perú UTC-5) en lugar de UTC
   */
  const calcularRangoFechas = useCallback(() => {
    const hoy = new Date();
    const ayer = new Date(hoy);
    ayer.setDate(hoy.getDate() - 1);

    const inicioSemana = new Date(hoy);
    inicioSemana.setDate(hoy.getDate() - hoy.getDay());

    const inicioMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);

    switch (filtroFecha) {
      case 'hoy':
        return {
          inicio: formatearFechaLocal(hoy),
          fin: formatearFechaLocal(hoy)
        };
      case 'ayer':
        return {
          inicio: formatearFechaLocal(ayer),
          fin: formatearFechaLocal(ayer)
        };
      case 'semana':
        return {
          inicio: formatearFechaLocal(inicioSemana),
          fin: formatearFechaLocal(hoy)
        };
      case 'mes':
        return {
          inicio: formatearFechaLocal(inicioMes),
          fin: formatearFechaLocal(hoy)
        };
      case 'personalizado':
        return {
          inicio: fechaInicio,
          fin: fechaFin
        };
      default:
        return { inicio: null, fin: null };
    }
  }, [filtroFecha, fechaInicio, fechaFin]);

  /**
   * Agrupa las órdenes por estado
   */
  const agruparPorEstado = useCallback((ordenes) => {
    const agrupadas = {};

    estados.forEach(estado => {
      agrupadas[estado.id] = ordenes.filter(o => o.id_estado_servicio === estado.id);
    });

    return agrupadas;
  }, [estados]);

  /**
   * Actualiza una orden en el estado local
   */
  const actualizarOrdenes = useCallback((ordenActualizada) => {
    setOrdenes(prevOrdenes => {
      const nuevasOrdenes = { ...prevOrdenes };

      // Remover la orden de todos los estados
      Object.keys(nuevasOrdenes).forEach(estadoKey => {
        nuevasOrdenes[estadoKey] = (nuevasOrdenes[estadoKey] || []).filter(
          orden => orden.id_orden_servicio !== ordenActualizada.id_orden_servicio
        );
      });

      // Agregar la orden al nuevo estado
      const estadoId = ordenActualizada.id_estado_servicio;

      if (!nuevasOrdenes[estadoId]) {
        nuevasOrdenes[estadoId] = [];
      }

      nuevasOrdenes[estadoId] = [
        ordenActualizada,
        ...nuevasOrdenes[estadoId]
      ];

      return nuevasOrdenes;
    });
  }, []);

  /**
   * Obtiene la lista de locales
   */
  const fetchLocales = useCallback(async () => {
    try {
      const response = await axios.get(`${appConfig.API_BASE_URL}/api/locales`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setLocales(response.data);
    } catch (error) {
      console.error('Error al obtener locales:', error);
    }
  }, [user?.token]);

  /**
   * Obtiene la lista de trabajadores
   */
  const fetchTrabajadores = useCallback(async () => {
    try {
      const response = await axios.get(`${appConfig.API_BASE_URL}/api/trabajadores`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setTrabajadores(response.data);
    } catch (error) {
      console.error('Error al obtener trabajadores:', error);
    }
  }, [user?.token]);

  /**
   * Obtiene las órdenes según los filtros aplicados
   */
  const fetchOrdenes = useCallback(async () => {
    if (!user?.token) return;

    setLoading(true);
    try {
      let url = `${appConfig.API_BASE_URL}/api/ordenes`;
      const params = new URLSearchParams();

      const fechas = calcularRangoFechas();
      if (fechas.inicio && fechas.fin) {
        params.append('fecha_inicio', fechas.inicio);
        params.append('fecha_fin', fechas.fin);
      }

      if (filtroLocal) {
        params.append('local', filtroLocal);
      }

      if (filtroTrabajador) {
        params.append('trabajador', filtroTrabajador);
      }

      if (filtroCliente) {
        params.append('cliente', filtroCliente);
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      console.log('Fetching ordenes from:', url);

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${user.token}` }
      });

      // Filtrar por cliente en frontend si el backend no lo soporta
      let ordenesFinales = response.data;
      if (filtroCliente) {
        const searchTerm = filtroCliente.toLowerCase();
        ordenesFinales = ordenesFinales.filter(orden =>
          (orden.nombre_cliente && orden.nombre_cliente.toLowerCase().includes(searchTerm)) ||
          (orden.apellido_cliente && orden.apellido_cliente.toLowerCase().includes(searchTerm))
        );
      }

      const agrupadas = agruparPorEstado(ordenesFinales);
      setOrdenes(agrupadas);
    } catch (error) {
      console.error('Error al obtener las órdenes:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.token, calcularRangoFechas, filtroLocal, filtroTrabajador, filtroCliente, agruparPorEstado]);

  // Debug: Log estados cuando cambian
  useEffect(() => {
    console.log('TableroControl - Estados cargados:', estados);
    console.log('TableroControl - Loading estados:', estadosLoading);
  }, [estados, estadosLoading]);

  // Cargar datos iniciales
  useEffect(() => {
    if (user?.token) {
      fetchOrdenes();
      fetchLocales();
      fetchTrabajadores();
    }
  }, [user?.token, fetchOrdenes, fetchLocales, fetchTrabajadores]);

  // Unirse a sala de empresa (solo una vez por empresa)
  useEffect(() => {
    if (!socket || !user?.id_empresa) return;

    // Si ya estamos unidos a esta empresa, no hacer nada
    if (joinedCompanyRef.current && currentCompanyRef.current === user.id_empresa) {
      return;
    }

    // Si cambiamos de empresa, salir de la anterior
    if (currentCompanyRef.current && currentCompanyRef.current !== user.id_empresa) {
      console.log('🔌 Saliendo de sala anterior:', currentCompanyRef.current);
      socket.emit('leaveCompany', currentCompanyRef.current);
    }

    // Unirse a la nueva sala
    console.log('🔌 Uniéndose a sala de empresa:', user.id_empresa);
    socket.emit('joinCompany', user.id_empresa);
    joinedCompanyRef.current = true;
    currentCompanyRef.current = user.id_empresa;

    // Cleanup solo cuando se desmonte el componente
    return () => {
      if (currentCompanyRef.current) {
        console.log('🔌 Desmontando - Saliendo de sala:', currentCompanyRef.current);
        socket.emit('leaveCompany', currentCompanyRef.current);
        joinedCompanyRef.current = false;
        currentCompanyRef.current = null;
      }
    };
  }, [socket, user?.id_empresa]);

  // Socket listeners con validación de empresa
  useEffect(() => {
    if (!socket) return;

    const handleOrdenActualizada = (ordenActualizada) => {
      if (ordenActualizada.id_empresa !== empresaIdRef.current) {
        console.log('⚠️ Evento ignorado - orden de empresa diferente:', ordenActualizada.id_empresa);
        return;
      }
      console.log('✅ Orden actualizada en tablero:', ordenActualizada);
      actualizarOrdenes(ordenActualizada);
      setUltimaActualizacion(new Date());
    };

    const handleOrdenAsignada = (ordenActualizada) => {
      if (ordenActualizada.id_empresa !== empresaIdRef.current) {
        console.log('⚠️ Evento ignorado - asignación de empresa diferente');
        return;
      }
      console.log('✅ Nueva asignación en tablero:', ordenActualizada);
      actualizarOrdenes(ordenActualizada);
      setUltimaActualizacion(new Date());
    };

    const handleServicioAprobado = (data) => {
      if (data?.id_empresa && data.id_empresa !== empresaIdRef.current) {
        console.log('⚠️ Evento ignorado - aprobación de empresa diferente');
        return;
      }
      console.log('✅ Servicio aprobado - refrescando tablero');
      fetchOrdenes();
      setUltimaActualizacion(new Date());
    };

    const handleServiciosAprobadosMasivo = () => {
      console.log('✅ Servicios aprobados masivamente - refrescando tablero');
      fetchOrdenes();
      setUltimaActualizacion(new Date());
    };

    socket.on('ordenActualizada', handleOrdenActualizada);
    socket.on('ordenAsignada', handleOrdenAsignada);
    socket.on('servicioAprobado', handleServicioAprobado);
    socket.on('serviciosAprobadosMasivo', handleServiciosAprobadosMasivo);

    return () => {
      socket.off('ordenActualizada', handleOrdenActualizada);
      socket.off('ordenAsignada', handleOrdenAsignada);
      socket.off('servicioAprobado', handleServicioAprobado);
      socket.off('serviciosAprobadosMasivo', handleServiciosAprobadosMasivo);
    };
  }, [socket, fetchOrdenes, actualizarOrdenes]);

  /**
   * Calcula las estadísticas del tablero
   */
  const calcularEstadisticas = () => {
    const totalOrdenes = Object.values(ordenes).flat().length;
    // Firmado (8), Revisado (9) y Liquidado (10) se consideran completadas
    const ordenesCompletadas = (ordenes[5] || []).length + (ordenes[8] || []).length + (ordenes[9] || []).length + (ordenes[10] || []).length;
    const ordenesPendientes = (ordenes[1] || []).length + (ordenes[6] || []).length;
    const ordenesEnProceso = (ordenes[2] || []).length + (ordenes[3] || []).length + (ordenes[4] || []).length;

    return {
      total: totalOrdenes,
      completadas: ordenesCompletadas,
      pendientes: ordenesPendientes,
      enProceso: ordenesEnProceso,
      porcentajeCompletado: totalOrdenes > 0 ? Math.round((ordenesCompletadas / totalOrdenes) * 100) : 0
    };
  };

  const estadisticas = calcularEstadisticas();

  // Mostrar loading mientras se carga la configuración
  if (configLoading || estadosLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Cargando configuración...</Typography>
      </Box>
    );
  }

  // Si no hay configuración o estados, mostrar mensaje
  if (!config || estados.length === 0) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          No hay estados configurados para su empresa. Contacte al administrador.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{
      width: '100%',
      minHeight: '100%',
      p: { xs: 1, sm: 2, md: 3 }
    }}>
      {/* Header */}
      <Paper sx={{
        p: { xs: 2, sm: 2.5 },
        mb: 2.5,
        borderRadius: 3,
        border: '1px solid #E2E8F0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
      }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 2.5
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton size="small" onClick={() => navigate(-1)}
              sx={{ display: { xs: 'none', sm: 'flex' }, color: '#1A237E' }}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant={isMobile ? "h6" : "h5"} fontWeight={800} color="#1A237E">
                Tablero de Control
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Monitoreo en tiempo real
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              {ultimaActualizacion.toLocaleTimeString('es-PE')}
            </Typography>
            <IconButton size="small" onClick={fetchOrdenes}
              sx={{ bgcolor: '#1A237E', color: '#fff', '&:hover': { bgcolor: '#0D47A1' } }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Filtros */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Filtrar por Fecha</InputLabel>
              <Select
                value={filtroFecha}
                onChange={(e) => setFiltroFecha(e.target.value)}
                label="Filtrar por Fecha"
              >
                <MenuItem value="hoy">Hoy</MenuItem>
                <MenuItem value="ayer">Ayer</MenuItem>
                <MenuItem value="semana">Esta Semana</MenuItem>
                <MenuItem value="mes">Este Mes</MenuItem>
                <MenuItem value="personalizado">Personalizado</MenuItem>
                <MenuItem value="todos">Todos</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {filtroFecha === 'personalizado' && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Fecha Inicio"
                  value={fechaInicio}
                  onChange={(e) => setFechaInicio(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Fecha Fin"
                  value={fechaFin}
                  onChange={(e) => setFechaFin(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Filtrar por Local</InputLabel>
              <Select
                value={filtroLocal}
                onChange={(e) => setFiltroLocal(e.target.value)}
                label="Filtrar por Local"
              >
                <MenuItem value="">Todos los locales</MenuItem>
                {locales.map((local) => (
                  <MenuItem key={local.id_local} value={local.id_local}>
                    {local.nombre_local}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Filtrar por Trabajador</InputLabel>
              <Select
                value={filtroTrabajador}
                onChange={(e) => setFiltroTrabajador(e.target.value)}
                label="Filtrar por Trabajador"
              >
                <MenuItem value="">Todos los trabajadores</MenuItem>
                {trabajadores.map((trabajador) => (
                  <MenuItem key={trabajador.id_trabajador} value={trabajador.id_trabajador}>
                    {trabajador.nombre_trabajador} {trabajador.apellido_trabajador}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Buscar Cliente"
              value={filtroCliente}
              onChange={(e) => setFiltroCliente(e.target.value)}
              placeholder="Nombre o apellido..."
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Estadísticas */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <EstadisticasCard titulo="Total" valor={estadisticas.total} icono={<AssessmentIcon />} color="#1A237E" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <EstadisticasCard titulo="Completadas" valor={estadisticas.completadas} icono={<CheckCircleIcon />} color="#10B981" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <EstadisticasCard titulo="En Proceso" valor={estadisticas.enProceso} icono={<EngineeringIcon />} color="#3B82F6" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <EstadisticasCard titulo="Pendientes" valor={estadisticas.pendientes} icono={<PendingIcon />} color="#EF4444" />
        </Grid>
      </Grid>

      {/* Tablero Kanban */}
      <Grid container spacing={1.5}>
        {estados.map((estado) => (
          <KanbanColumn
            key={estado.id}
            estado={estado}
            ordenes={ordenes[estado.id] || []}
            onViewOrden={(orden) => {
              setSelectedOrden(orden);
              setOpenDetail(true);
            }}
            onUpdateOrden={(orden) => {
              setSelectedOrden(orden);
              setOpenUpdate(true);
            }}
          />
        ))}
      </Grid>

      {/* Loading overlay mejorado */}
      {loading && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: 'rgba(26, 35, 126, 0.1)',
          backdropFilter: 'blur(5px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <Paper sx={{
            p: 4,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
            border: '1px solid rgba(26, 35, 126, 0.2)',
            textAlign: 'center'
          }}>
            <Box sx={{ mb: 2 }}>
              <CircularProgress size={50} sx={{ color: '#1A237E' }} />
            </Box>
            <Typography variant="h6" sx={{ color: '#1A237E', fontWeight: 'bold' }}>
              Actualizando Tablero...
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Diálogos */}
      <DetalleDialog
        open={openDetail}
        onClose={() => setOpenDetail(false)}
        ordenId={selectedOrden?.id_orden_servicio}
      />

      <ActualizarEstadoDialog
        open={openUpdate}
        onClose={() => setOpenUpdate(false)}
        orden={selectedOrden}
        onUpdateSuccess={fetchOrdenes}
        estados={estados}
      />
    </Box>
  );
}

export default TableroControl;
